import { useMemo, useState } from "react";
import { Search } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { EmptyState, Spinner, ago } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

/** Who did what. Never values — an entry records that a key changed, not to what. */
export function Audit() {
    const [entries, setEntries] = useState(null);
    const [{ q: query }, setUrl] = useUrlStates({ q: "" });
    const setQuery = (value) => setUrl("q", value);

    // Every write to the log is announced, so the newest entry is already here.
    useLive(["audit"], () => api.audit({ limit: 200 }).then(setEntries).catch(() => setEntries([])));

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return entries ?? [];
        return (entries ?? []).filter((entry) =>
            `${entry.actor} ${entry.action} ${entry.slug ?? ""} ${entry.target ?? ""}`.toLowerCase().includes(needle)
        );
    }, [entries, query]);

    if (!entries) return <Spinner className="muted" />;

    // A refused sign-in and a reused device cookie are what somebody scanning this
    // page is looking for, so they read as red like a denial does.
    const denied = (entry) => ["denied", "rejected", "failed", "reuse"].some((word) => entry.action.includes(word));

    return (
        <>
            <PageHeader
                title="Audit"
                meta={query ? `${shown.length} of ${entries.length}` : `${entries.length} entries`}
                actions={
                    <div className="relative w-full sm:w-64">
                        <Search size={14} className="muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
                        <input
                            className="input pl-8"
                            placeholder="Search actor, action or project"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            aria-label="Search the audit log"
                        />
                    </div>
                }
            />

            <DataTable
                rows={shown}
                rowKey={(entry) => entry.id}
                caption="Audit log"
                empty={<div className="card"><EmptyState title="Nothing matches" hint="Try a different search." /></div>}
                columns={[
                    {
                        key: "action",
                        header: "Action",
                        primary: true,
                        cell: (entry) => (
                            <span className="flex flex-wrap items-baseline gap-x-2">
                                <code className={`font-mono text-xs ${denied(entry) ? "text-red-600 dark:text-red-400" : ""}`}>{entry.action}</code>
                                <span className="muted text-xs">{entry.slug ?? entry.target ?? ""}</span>
                            </span>
                        )
                    },
                    { key: "actor", header: "Actor", cell: (entry) => <span className="font-mono text-xs">{entry.actor}</span> },
                    { key: "target", header: "Target", hideBelow: "lg", cell: (entry) => entry.slug ?? entry.target ?? "—" },
                    {
                        key: "detail",
                        header: "Detail",
                        hideBelow: "xl",
                        cell: (entry) => (
                            <span className="muted block max-w-md truncate text-xs" title={JSON.stringify(entry.meta ?? {})}>
                                {Object.keys(entry.meta ?? {}).length ? JSON.stringify(entry.meta) : "—"}
                            </span>
                        )
                    },
                    { key: "when", header: "When", align: "right", cell: (entry) => <span className="muted whitespace-nowrap">{ago(entry.at)}</span> }
                ]}
            />
        </>
    );
}
